import React from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const TreatmentTimeline = ({ treatmentId, treatmentData }) => {
  const timelineEvents = [
    {
      id: 1,
      date: '2025-11-15',
      title: 'Initial Consultation',
      description: 'Comprehensive examination and treatment planning',
      status: 'completed',
      provider: treatmentData?.provider || 'Dr. Michael Chen'
    },
    {
      id: 2,
      date: '2025-11-22',
      title: 'Pre-Treatment Preparation',
      description: 'X-rays, impressions, and treatment area preparation',
      status: 'completed',
      provider: treatmentData?.provider || 'Dr. Michael Chen'
    },
    {
      id: 3,
      date: '2025-12-05',
      title: 'Primary Treatment Procedure',
      description: treatmentData?.label || 'Main treatment procedure completed',
      status: 'completed',
      provider: treatmentData?.provider || 'Dr. Michael Chen',
      highlight: true
    },
    {
      id: 4,
      date: '2025-12-12',
      title: 'First Follow-up Visit',
      description: 'Post-treatment assessment and healing check',
      status: 'completed',
      provider: treatmentData?.provider || 'Dr. Michael Chen'
    },
    {
      id: 5,
      date: treatmentData?.completedDate || '2026-01-05',
      title: 'Treatment Completion',
      description: 'Final assessment and treatment completion',
      status: 'completed',
      provider: treatmentData?.provider || 'Dr. Michael Chen'
    },
    {
      id: 6,
      date: '2026-02-05',
      title: 'Recommended Follow-up',
      description: '30-day post-treatment evaluation',
      status: 'scheduled',
      provider: treatmentData?.provider || 'Dr. Michael Chen'
    }
  ];

  const followUpRecommendations = [
    {
      id: 1,
      title: '30-Day Follow-up',
      description: 'Comprehensive evaluation of treatment outcomes',
      dueDate: '2026-02-05',
      priority: 'high'
    },
    {
      id: 2,
      title: '6-Month Check-up',
      description: 'Long-term outcome assessment and maintenance planning',
      dueDate: '2026-07-05',
      priority: 'medium'
    },
    {
      id: 3,
      title: 'Annual Review',
      description: 'Yearly evaluation and preventive care assessment',
      dueDate: '2027-01-05',
      priority: 'low'
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-success/10 text-success border-success';
      case 'scheduled':
        return 'bg-primary/10 text-primary border-primary';
      case 'pending':
        return 'bg-warning/10 text-warning border-warning';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'bg-error/10 text-error border-error';
      case 'medium':
        return 'bg-warning/10 text-warning border-warning';
      case 'low':
        return 'bg-primary/10 text-primary border-primary';
      default:
        return 'bg-muted text-muted-foreground border-border';
    }
  };

  return (
    <div className="space-y-8">
      <div>
        <h3 className="text-lg font-semibold text-foreground mb-2 flex items-center gap-2">
          <Icon name="Clock" size={20} className="text-primary" />
          Treatment Timeline
        </h3>
        <p className="text-sm text-muted-foreground mb-6">
          Review your complete treatment journey and upcoming appointments
        </p>

        <div className="relative">
          {/* Timeline line */}
          <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-border" />

          <div className="space-y-6">
            {timelineEvents?.map((event, index) => (
              <div key={event?.id} className="relative pl-12">
                {/* Timeline dot */}
                <div
                  className={`absolute left-0 w-8 h-8 rounded-full border-2 flex items-center justify-center ${
                    event?.status === 'completed'
                      ? 'bg-success border-success'
                      : event?.status === 'scheduled' ?'bg-primary border-primary' :'bg-muted border-border'
                  }`}
                >
                  {event?.status === 'completed' ? (
                    <Icon name="Check" size={16} className="text-white" />
                  ) : event?.status === 'scheduled' ? (
                    <Icon name="Calendar" size={16} className="text-white" />
                  ) : (
                    <Icon name="Clock" size={16} className="text-muted-foreground" />
                  )}
                </div>

                {/* Event card */}
                <div
                  className={`bg-card rounded-lg border p-4 ${
                    event?.highlight ? 'border-primary shadow-elevation-2' : 'border-border'
                  }`}
                >
                  <div className="flex items-start justify-between mb-2">
                    <div>
                      <h4 className="font-semibold text-foreground">{event?.title}</h4>
                      <p className="text-sm text-muted-foreground mt-1">{event?.description}</p>
                    </div>
                    <span
                      className={`inline-flex items-center px-2 py-1 rounded-md text-xs font-medium border ${getStatusColor(
                        event?.status
                      )}`}
                    >
                      {event?.status}
                    </span>
                  </div>
                  <div className="flex items-center gap-4 text-xs text-muted-foreground mt-3">
                    <span className="flex items-center gap-1">
                      <Icon name="Calendar" size={14} />
                      {event?.date}
                    </span>
                    <span className="flex items-center gap-1">
                      <Icon name="User" size={14} />
                      {event?.provider}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="border-t border-border pt-6">
        <h3 className="text-lg font-semibold text-foreground mb-6 flex items-center gap-2">
          <Icon name="CalendarCheck" size={20} className="text-primary" />
          Follow-up Recommendations
        </h3>
        <div className="space-y-4">
          {followUpRecommendations?.map(recommendation => (
            <div
              key={recommendation?.id}
              className="bg-card rounded-lg border border-border p-4 hover:shadow-elevation-1 transition-shadow"
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <h4 className="font-semibold text-foreground">{recommendation?.title}</h4>
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${getPriorityColor(
                        recommendation?.priority
                      )}`}
                    >
                      {recommendation?.priority} priority
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">{recommendation?.description}</p>
                </div>
              </div>
              <div className="flex items-center justify-between mt-3">
                <span className="text-xs text-muted-foreground flex items-center gap-1">
                  <Icon name="Calendar" size={14} />
                  Due: {recommendation?.dueDate}
                </span>
                <Button variant="outline" size="sm" iconName="Calendar">
                  Schedule Appointment
                </Button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div className="border-t border-border pt-6">
        <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
          <Icon name="FileText" size={20} className="text-primary" />
          Treatment Summary
        </h3>
        <div className="bg-muted/50 rounded-lg border border-border p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
            <div>
              <span className="text-muted-foreground">Treatment Duration:</span>
              <p className="font-medium text-foreground">51 days</p>
            </div>
            <div>
              <span className="text-muted-foreground">Total Visits:</span>
              <p className="font-medium text-foreground">5 appointments</p>
            </div>
            <div>
              <span className="text-muted-foreground">Next Appointment:</span>
              <p className="font-medium text-foreground">2026-02-05</p>
            </div>
          </div>
        </div>
      </div>

      <div className="bg-primary/5 rounded-lg border border-primary/20 p-4">
        <div className="flex items-start gap-3">
          <Icon name="Info" size={20} className="text-primary flex-shrink-0 mt-0.5" />
          <div className="text-sm text-muted-foreground">
            <p className="font-medium text-foreground mb-1">Ongoing Care</p>
            <p>
              Regular follow-up appointments are essential for monitoring your treatment outcomes and ensuring long-term
              success. Please schedule your recommended appointments to maintain optimal oral health.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TreatmentTimeline;